import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { CollectionsShell } from './CollectionsShell';
import type { ArTabId } from './CollectionsShell';
import { fmtMoney } from '../Collections/CollectionsView';
import { AR_LINK_COUNTS, AR_LINK_EYEBROW, AR_LINK_FOLLOWUPS, AR_LINK_FOOTER, AR_LINK_ROWS } from '../../ar/mockData';
import type { ClxChannel, ClxStatus, PaymentLinkRow } from '../../collections/types';

export interface ArPaymentLinksViewProps {
  onSelectTab?: (tab: ArTabId) => void;
  onOpenSettings?: () => void;
  onCreateLink?: () => void;
  onOpenPatient?: (patientId: string) => void;
}

const CHANNEL_ICON: Record<ClxChannel, IconKind> = { text: 'chat', email: 'mail', qr: 'grid' };

const STATUS_PILL: Record<ClxStatus, { label: string; fg: string; bg: string }> = {
  paid: { label: 'Paid', fg: 'var(--money-green)', bg: 'var(--money-green-tint)' },
  waiting: { label: 'Waiting', fg: 'var(--brand-indigo)', bg: 'var(--brand-indigo-tint)' },
  expired: { label: 'Expired', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' },
  canceled: { label: 'Cancelled', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' },
};

const COLS = 'grid-cols-[minmax(0,1.4fr)_96px_minmax(0,1.2fr)_minmax(0,1.2fr)_96px_minmax(0,1.5fr)]';

/** 'anyone' | 'team' | 'follow-ups' | a follow-up name. */
type SentByFilter = string;

/**
 * Collections › Payment links (amendment 28 §2.3, board `AR5PaymentLinks`).
 *
 * Spec 12's table, now carrying **both** the team's links and the follow-ups'
 * links, with a **Sent by** column and filter. The chip counts are the true
 * totals (94 = 21 + 58 + 13 + 2) and the footer splits the $10,700.
 */
export function ArPaymentLinksView({ onSelectTab, onOpenSettings, onCreateLink, onOpenPatient }: ArPaymentLinksViewProps) {
  const [filter, setFilter] = useState<'all' | ClxStatus>('all');
  const [sentBy, setSentBy] = useState<SentByFilter>('anyone');

  const chips: { id: 'all' | ClxStatus; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: AR_LINK_COUNTS.all },
    { id: 'waiting', label: 'Waiting', count: AR_LINK_COUNTS.waiting },
    { id: 'paid', label: 'Paid', count: AR_LINK_COUNTS.paid },
    { id: 'expired', label: 'Expired', count: AR_LINK_COUNTS.expired },
    { id: 'canceled', label: 'Cancelled', count: AR_LINK_COUNTS.cancelled },
  ];

  const rows = AR_LINK_ROWS.filter((r) => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (sentBy === 'anyone') return true;
    if (sentBy === 'team') return r.sentBy?.kind === 'team';
    if (sentBy === 'follow-ups') return r.sentBy?.kind === 'follow-up';
    return r.sentBy?.kind === 'follow-up' && r.sentBy.label === sentBy;
  });

  return (
    <CollectionsShell
      active="links"
      eyebrow={`Collections · ${AR_LINK_EYEBROW}`}
      onSelectTab={onSelectTab}
      onOpenSettings={onOpenSettings}
      onCreateLink={onCreateLink}
      createIsPrimary
    >
      {/* filter row */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <Chip key={c.id} label={c.label} count={c.count} on={filter === c.id} onClick={() => { setFilter(c.id); }} />
        ))}
        <div className="ml-auto">
          <label className="sr-only" htmlFor="ar-sentby">Sent by</label>
          <select
            id="ar-sentby"
            value={sentBy}
            onChange={(e) => { setSentBy(e.target.value); }}
            className="rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          >
            <option value="anyone">Sent by · Anyone</option>
            <option value="team">Your team</option>
            <option value="follow-ups">Follow-ups</option>
            {AR_LINK_FOLLOWUPS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      {/* table */}
      <div className="mt-3 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className={cn('grid items-center gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-header)] px-3 py-2.5 font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]', COLS)}>
          <span>Patient</span>
          <span className="text-right">Amount</span>
          <span>Sent to</span>
          <span>Sent by</span>
          <span>Status</span>
          <span>What happened</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-[12.5px] text-[var(--text-muted)]">No links match.</div>
        ) : (
          rows.map((r) => <Row key={r.id} r={r} onOpenPatient={onOpenPatient} />)
        )}
      </div>

      {/* footer (§2.3) */}
      <div className="mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{AR_LINK_FOOTER}</div>
    </CollectionsShell>
  );
}

function Row({ r, onOpenPatient }: { r: PaymentLinkRow; onOpenPatient?: ((id: string) => void) | undefined }) {
  const pill = STATUS_PILL[r.status];
  return (
    <button
      type="button"
      onClick={() => { onOpenPatient?.(r.patientId); }}
      className={cn('grid w-full items-center gap-x-3 border-t border-[var(--border-default)] px-3 py-3.5 text-left text-[13px] leading-[1.5] hover:bg-[var(--surface-sunken)]', COLS)}
    >
      <div className="min-w-0">
        <div className="truncate font-semibold text-[var(--text-primary)]">{r.patient}</div>
        <div className="truncate text-[11.5px] text-[var(--text-muted)]">{r.label ?? '—'}</div>
      </div>
      <div className="text-right font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">{fmtMoney(r.amount)}</div>
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon kind={CHANNEL_ICON[r.channel]} size={13} stroke={1.9} style={{ color: 'var(--icon-default)' }} />
        <span className="truncate text-[var(--text-secondary)]">{r.destination}</span>
      </div>
      <div className="min-w-0 truncate">
        {r.sentBy?.kind === 'follow-up' ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--brand-indigo)]">
            <Icon kind="sparkle" size={11} stroke={2} /> Follow-up · {r.sentBy.label}
          </span>
        ) : (
          <span className="text-[12.5px] text-[var(--text-secondary)]">{r.sentBy?.label ?? '—'}</span>
        )}
      </div>
      <div>
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ color: pill.fg, background: pill.bg }}>{pill.label}</span>
      </div>
      <div className="min-w-0">
        <div className="truncate">
          <span className={cn('font-semibold', r.status === 'paid' ? 'text-[var(--money-green)]' : 'text-[var(--text-primary)]')}>{r.outcomeLead}</span>
          {r.outcomeExpiry !== undefined ? <span className="text-[var(--text-muted)]"> · {r.outcomeExpiry}</span> : null}
        </div>
        <div className="truncate text-[11.5px] text-[var(--text-muted)]">{r.outcomeSub}</div>
      </div>
    </button>
  );
}

function Chip({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold',
        on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo)] text-[var(--text-on-accent)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {label}
      <span className={cn('font-[var(--font-mono)] text-[11px] font-bold', on ? 'text-[var(--text-on-accent)]' : 'text-[var(--text-muted)]')}>{count}</span>
    </button>
  );
}
